/**
 * @fileoverview Self-signup registration page.
 * Collects user details (name, email, password), validates client-side,
 * submits to the backend, and shows a "check your email" confirmation.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { validatePassword, isValidEmail } from '../utils/validation';
import Loading from '../components/Loading';
import PasswordInput from '../components/PasswordInput';

/**
 * Signup page component.
 * Renders a registration form with client-side validation.
 * On successful registration, shows a "check your email" screen.
 * If already authenticated, redirects to /.
 *
 * @returns {JSX.Element} The signup page
 */
function Signup() {
  const { authenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    consent_accepted: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const successRef = useRef(null);
  const [gateLoading, setGateLoading] = useState(true);

  useEffect(() => {
    if (success) successRef.current?.focus();
  }, [success]);

  /**
   * On mount, check if registration requires an access code.
   * If a code is required and no registration token exists in sessionStorage,
   * redirect to the access code gate page.
   */
  useEffect(() => {
    let cancelled = false;

    async function checkGate() {
      try {
        const res = await api.getRegistrationStatus();
        if (!cancelled && res.data.requires_code) {
          const token = sessionStorage.getItem('registration_token');
          if (!token) {
            navigate('/register-access', { replace: true });
            return;
          }
        }
      } catch {
        // If the check fails, allow signup to proceed
      } finally {
        if (!cancelled) setGateLoading(false);
      }
    }

    checkGate();
    return () => { cancelled = true; };
  }, [navigate]);

  // Authenticated users don't need to sign up
  if (authenticated) {
    return <Navigate to="/" replace />;
  }

  /**
   * Handle form field changes and clear per-field errors on change.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error for the changed field
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (apiError) setApiError('');
  };

  /**
   * Validate all form fields and return an errors object.
   *
   * @returns {Object} Map of field name to error message (empty if valid)
   */
  const validate = () => {
    const newErrors = {};
    if (!form.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!form.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(form.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!form.password) {
      newErrors.password = 'Password is required';
    } else {
      const pwError = validatePassword(form.password);
      if (pwError) newErrors.password = pwError;
    }
    if (!form.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (form.password !== form.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    if (!form.consent_accepted) {
      newErrors.consent_accepted = 'You must accept the Terms of Service and Privacy Policy to continue';
    }
    return newErrors;
  };

  /**
   * Handle form submission — validate, call API, and show success or errors.
   *
   * @param {React.FormEvent} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setApiError('');
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        confirm_password: form.confirm_password,
        consent_accepted: form.consent_accepted,
      };
      const registrationToken = sessionStorage.getItem('registration_token');
      if (registrationToken) {
        payload.registration_token = registrationToken;
      }
      await api.register(payload);
      sessionStorage.removeItem('registration_token');
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 403) {
        sessionStorage.removeItem('registration_token');
        navigate('/register-access');
        return;
      } else if (err.response?.status === 409) {
        setApiError('An account with this email already exists');
      } else {
        setApiError(
          err.response?.data?.error || 'Registration failed. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Resend the verification email for the just-registered address.
   */
  const handleResend = async () => {
    setResending(true);
    setResendMessage('');
    try {
      await api.resendVerification({ email: form.email.trim().toLowerCase() });
      setResendMessage('Verification email sent!');
    } catch {
      setResendMessage('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Show loading while gate check is in progress
  if (gateLoading) {
    return (
      <div className="signup-page">
        <div className="signup-card">
          <Loading variant="spinner" size="sm" message="Loading..." />
        </div>
      </div>
    );
  }

  // Success screen — "check your email"
  if (success) {
    return (
      <div className="signup-page">
        <div className="signup-card">
          <div className="signup-success">
            <a href="https://cogslog.com" aria-label="COGSLOG home">
              <img src="/logos/stacked/cogslog-stacked-tagline-color.svg" alt="COGSLOG" className="auth-logo" />
            </a>
            <svg
              className="signup-success-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <h2 ref={successRef} tabIndex={-1}>Check Your Email</h2>
            <p>
              We sent a verification link to <strong>{form.email}</strong>.
              Please check your inbox and click the link to verify your account.
            </p>
            {resendMessage && (
              <p className="signup-resend-message">{resendMessage}</p>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? 'Sending...' : 'Resend Verification Email'}
            </button>
            <p className="signup-signin-link">
              Already verified?{' '}
              <button type="button" className="btn btn-ghost" onClick={() => navigate('/login')}>
                Sign In
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-card">
        <div className="signup-header">
          <a href="https://cogslog.com" aria-label="COGSLOG home">
            <img
              src="/logos/stacked/cogslog-stacked-tagline-color.svg"
              alt="COGSLOG"
              className="auth-logo"
            />
          </a>
          <h1 className="signup-title">Create Your Account</h1>
          <p className="signup-subtitle">
            Get started with COGSLOG
          </p>
        </div>

        <form onSubmit={handleSubmit} className="signup-form" noValidate>
          {apiError && (
            <div className="err-msg" role="alert">
              {apiError}
            </div>
          )}

          <div className="signup-row">
            <div className="field">
              <label htmlFor="first_name" className="label">First Name</label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                className="input"
                value={form.first_name}
                onChange={handleChange}
                disabled={submitting}
                autoFocus
                aria-invalid={!!errors.first_name}
                aria-describedby={errors.first_name ? 'first_name-error' : undefined}
              />
              {errors.first_name && (
                <span id="first_name-error" className="err-msg">{errors.first_name}</span>
              )}
            </div>
            <div className="field">
              <label htmlFor="last_name" className="label">Last Name</label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                className="input"
                value={form.last_name}
                onChange={handleChange}
                disabled={submitting}
                aria-invalid={!!errors.last_name}
                aria-describedby={errors.last_name ? 'last_name-error' : undefined}
              />
              {errors.last_name && (
                <span id="last_name-error" className="err-msg">{errors.last_name}</span>
              )}
            </div>
          </div>

          <div className="field">
            <label htmlFor="email" className="label">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              className="input"
              value={form.email}
              onChange={handleChange}
              disabled={submitting}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <span id="email-error" className="err-msg">{errors.email}</span>
            )}
          </div>

          <div className="field">
            <label htmlFor="password" className="label">Password</label>
            <PasswordInput
              id="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              disabled={submitting}
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
            />
            {errors.password && (
              <span id="password-error" className="err-msg">{errors.password}</span>
            )}
            <span className="hint">
              8+ characters, uppercase, lowercase, and a digit
            </span>
          </div>

          <div className="field">
            <label htmlFor="confirm_password" className="label">Confirm Password</label>
            <PasswordInput
              id="confirm_password"
              name="confirm_password"
              value={form.confirm_password}
              onChange={handleChange}
              disabled={submitting}
              aria-invalid={!!errors.confirm_password}
              aria-describedby={errors.confirm_password ? 'confirm_password-error' : undefined}
            />
            {errors.confirm_password && (
              <span id="confirm_password-error" className="err-msg">{errors.confirm_password}</span>
            )}
          </div>

          <div className="field">
            <label className="label label--checkbox">
              <input
                id="consent_accepted"
                name="consent_accepted"
                type="checkbox"
                checked={form.consent_accepted}
                onChange={(e) => setForm((prev) => ({ ...prev, consent_accepted: e.target.checked }))}
                disabled={submitting}
                aria-invalid={!!errors.consent_accepted}
                aria-describedby={errors.consent_accepted ? 'consent-error' : undefined}
              />
              I have read and agree to the{' '}
              <a href="https://cogslog.com/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="https://cogslog.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              <span className="req" aria-hidden="true">*</span>
            </label>
            {errors.consent_accepted && (
              <span id="consent-error" className="err-msg" role="alert">
                {errors.consent_accepted}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary signup-submit"
            disabled={submitting}
          >
            {submitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="signup-signin-link">
          Already have an account?{' '}
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/login')}>
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
}

Signup.propTypes = {};

export default Signup;
